import Fab from '@mui/material/Fab';
import { useTheme } from '@mui/material/styles';
import { SxProps, Theme, useMediaQuery } from '@mui/system';
import React from 'react';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { TitleAndClose } from 'src/components/TitleAndClose';

interface SidebarDialogLayoutProps {
  // Dialog props
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: React.ReactNode;
  leftAdornment?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  // Layout control
  showSidebar?: boolean;
  showFab?: boolean;

  // Content
  sidebarContent?: React.ReactNode;
  mainContent?: React.ReactNode;
  actions?: React.ReactNode;

  // FAB props
  fabContent?: React.ReactNode;
  fabDisabled?: boolean;
  fabActive?: boolean; // if true, use primary with enhanced styling; if false, use secondary
  fabOnClick?: () => void;
  fabSx?: SxProps<Theme>;
}

export const SidebarDialogLayout: React.FC<SidebarDialogLayoutProps> = ({
  open,
  onClose,
  title,
  subtitle,
  leftAdornment,
  maxWidth = 'md',
  showSidebar = false,
  showFab = false,
  sidebarContent,
  mainContent,
  actions,
  fabContent,
  fabDisabled = false,
  fabActive = false,
  fabOnClick,
  fabSx,
}) => {
  const theme = useTheme();
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const fabSxArray = [
    (theme) => ({
      pl: 2,
      height: '56px',
      borderRadius: '28px',
      backdropFilter: 'blur(5px)',
      textTransform: 'none',
      color: 'white',
      '&.Mui-disabled': {
        opacity: 1,
        color: theme.palette.neutral[50],
        backgroundColor: `rgba(${theme.vars.palette.neutral['500Channel']} / 0.9)`,
        boxShadow: '0px 12px 10px 0px rgba(102, 112, 133, 0.13)',
      },
    }),
    // Enhanced styling when active
    fabActive && {
      backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.9)`,
      boxShadow: `0px 12px 10px 0px rgba(
        ${theme.vars.palette.primary.mainChannel} / 0.4
      )`,
      ...theme.applyStyles('dark', {
        boxShadow: `0px 12px 10px 0px rgba(
          ${theme.vars.palette.primary.mainChannel} / 0.2
        )`,
      }),
    },
    ...(Array.isArray(fabSx) ? fabSx : [fabSx]),
  ];

  return (
    <Dialog
      open={open}
      fullScreen={mdDown}
      fullWidth
      maxWidth={maxWidth}
      actions={!showSidebar ? actions : undefined}
      spacing={!showSidebar ? 2 : 0}
      onClose={!showSidebar ? onClose : undefined}
      title={!showSidebar ? title : undefined}
      subtitle={!showSidebar ? subtitle : undefined}
      leftAdornment={!showSidebar ? leftAdornment : undefined}
    >
      {!showSidebar ? (
        <Box sx={{ pb: showFab ? '130px' : 4 }}>{mainContent}</Box>
      ) : (
        <Stack
          direction="row"
          sx={{
            alignItems: 'stretch',
            maxHeight: 'calc(100vh - 64px)',
            minHeight: '80vh',
          }}
        >
          <Box
            sx={{
              minWidth: '288px',
              borderRight: '1px solid',
              borderColor: 'divider',
              p: 2,
            }}
          >
            {sidebarContent}
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'nowrap',
            }}
          >
            <TitleAndClose
              onClose={onClose}
              title={title}
              subtitle={subtitle}
              leftAdornment={leftAdornment}
            />
            <Scroller direction="column" sx={{ px: 2, height: '100%' }}>
              <Box sx={{ pb: showFab ? '130px' : 4 }}>{mainContent}</Box>
            </Scroller>
            {actions && <Box p={2}>{actions}</Box>}
          </Box>
        </Stack>
      )}
      {showFab && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            bottom: '0px',
            width: '100%',
            position: mdDown ? 'fixed' : 'absolute',
            mb: 4,
          }}
        >
          <Fab
            size="large"
            variant="extended"
            color={fabActive ? 'primary' : 'secondary'}
            disabled={fabDisabled}
            onClick={fabOnClick}
            sx={fabSxArray}
          >
            {fabContent}
          </Fab>
        </Box>
      )}
    </Dialog>
  );
};
